/* ================================================================
   CULTURE 43.2 FM — Player Engine v3
   Reads: window.LF_PLAYLIST / window.LF_MIXES (from lf-playlist.js)
          window.LF_FM_SETTINGS (from lf-playlist.js)
          window.LF_FM_READY    (from lf-playlist.js — checkpoint 20)
          window.LF_CONFIG (from lf-config.liquid)
   Handles: playlist · mixes · stream · shuffle · queue · progress ·
            volume · Web Audio visualizer · error recovery

   CHECKPOINT 20 — RACE CONDITION FIX + MIX MODE:
   This file used to read window.LF_PLAYLIST synchronously the
   instant DOMContentLoaded fired, before lf-playlist.js's async
   fetch of playlist.json had resolved. That's why bulk-uploaded
   songs weren't reliably appearing. Every place that reads
   window.LF_PLAYLIST / window.LF_MIXES now happens only after
   `await window.LF_FM_READY`, so the data is guaranteed complete
   before the player ever builds a track list from it.

   Also new: a Playlist / Mixes source switch (state.source), and
   the "Up Next" queue list no longer hard-caps at 10 tracks — it
   shows the full library (the panel already scrolls).
================================================================ */

(function () {
  'use strict';

  /* Checkpoint 17/18: real SVG play/pause icons, shared across every
     place this file swaps the transport icon (main player, embedded
     widget, queue "now playing" marker) — replaces the old ▶/⏸ text
     glyphs, which don't render reliably on devices/fonts without
     emoji glyph support. */
  var LF_ICON_PLAY  = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  var LF_ICON_PAUSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';
  var LF_ICON_PLAY_SM = '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>';
  var LF_ICON_MIX_SM  = '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path fill="currentColor" d="M14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zM10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zm4.24 4.24l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/></svg>';

  function fmReady() {
    return window.LF_FM_READY || Promise.resolve();
  }

  /* Filter out entries with no playable src */
  function validTracks(list) {
    return (list || []).filter(function (t) { return t && t.src && t.src.length > 0; });
  }

  /* ── Wait for DOM ── */
  document.addEventListener('DOMContentLoaded', function () {

    /* ONE-TIME INIT GUARD — prevents double-binding on duplicate include */
    if (window.__lfFmPlayerBound) return;

    var settings  = window.LF_FM_SETTINGS  || {};
    var config    = (window.LF_CONFIG && window.LF_CONFIG.fm) || {};

    /* Determine engine mode: stream if config URL set, otherwise local
       playback from playlist.json / mixes.json. This is independent of
       WHICH local source (playlist vs mixes) is active. */
    var hasStream = config.stream && config.stream.indexOf('YOUR-STREAM-ID') === -1 && config.stream.length > 10;
    var mode = (settings.mode === 'stream' && hasStream) ? 'stream' : 'playlist';

    /* ──────────────────────────────────────────────────────────
       PLAYER STATE
    ────────────────────────────────────────────────────────── */
    var state = {
      mode:         mode,        /* 'stream' | 'playlist' — playback engine */
      source:       'playlist',  /* 'playlist' | 'mix' — which JSON dataset */
      playlist:     [],
      playOrder:    [],
      currentIdx:   0,
      isPlaying:    false,
      isShuffle:    !!settings.defaultShuffle,
      repeatMode:   'all',       /* 'off' | 'all' | 'one' — checkpoint 26 */
      volume:       0.8,
      audio:        null,
      bars:         [],
      root:         null,
      loadToken:    0,
      toggleToken:  0,
      searchQuery:  '',
      /* CHECKPOINT 28 — STUDIO FX. Persists across track switches on
         purpose (never reset in _loadTrack) — that's the "effects
         stay the same when the song switches" requirement.
         Bass + Reverb always run through the native <audio> element's
         own Web Audio routing. Pitch/Speed are back, engineered to
         avoid the old failure modes: Speed alone runs on the native
         element's own playbackRate + preservesPitch (instant, never
         glitches, works whether or not Pitch is engaged). The moment
         Pitch != 0, a SoundTouch PitchShifter is built ONCE for the
         current track (cached per-track buffer, never re-decoded on
         every slider tick) and takes over the audible signal while the
         native element keeps quietly playing (muted, not paused) so
         there's never a gap and its timeupdate/ended events keep
         driving the UI. */
      studio: {
        reverb:     0,          // 0..1 wet mix
        bassBoost:  0,          // 0..12 dB
        pitch:      0,          // semitones, -3..3
        speed:      1,          // multiplier, one of SPEED_STEPS
        engine:     'native',   // 'native' | 'soundtouch'
        ctx:        null,
        nativeSrcNode: null,    // MediaElementSourceNode — createMediaElementSource can only ever be called once per <audio>, so this is cached forever once created
        fxInput:    null,       // shared entry point into bass/reverb chain — whichever engine is live connects here
        bassNode:   null,
        dryGain:    null,
        wetGain:    null,
        convolver:  null,
        masterGain: null,
        shifter:    null,       // active SoundTouch PitchShifter, or null
        bufferCache:{},         // track.src -> Promise<AudioBuffer>, so re-adjusting pitch never re-fetches/re-decodes
        swapToken:  0,          // invalidates a stale async engine-swap if the user moves on before it finishes
        stLoadPromise: null,    // cached dynamic import() of the SoundTouch library
        wasBackgroundSwapped: false, // set true only when _swapToPlainAudioElement actually ran; gates the foreground SoundTouch rebuild so it doesn't fire on every visibility flicker
        lastPitchError: null    // real reason the last SoundTouch build failed, surfaced in the status line instead of a generic message
      }
    };

    /* Spotify/Apple-style stepped values — no free-drag sliders for
       Pitch/Speed, so every change is a single discrete, predictable
       step. Pitch in whole semitones; Speed covers the common range
       plus the finer 1.1/1.2/1.25 steps requested. */
    var SPEED_STEPS = [0.5, 0.75, 1, 1.1, 1.2, 1.25, 1.5, 2];
    var PITCH_STEPS = [-3, -2, -1, 0, 1, 2, 3];

    /* One-tap FX presets — quick, cool combos built entirely from
       controls that already exist (Speed/Pitch/Reverb/Bass), so they
       cost nothing extra to support and can never drift out of sync
       with the sliders/steppers, which update to match whenever a
       preset is applied. */
    var FX_PRESETS = {
      normal:        { speed: 1,    pitch: 0,  reverb: 0,   bass: 0  },
      nightcore:     { speed: 1.25, pitch: 3,  reverb: 0,   bass: 2  },
      slowedreverb:  { speed: 0.75, pitch: -2, reverb: 0.55,bass: 4  },
      vinyl:         { speed: 0.9,  pitch: -1, reverb: 0.2, bass: 6  },
      chipmunk:      { speed: 1.4,  pitch: 3,  reverb: 0,   bass: 0  }
    };

    /* ──────────────────────────────────────────────────────────
       DOM REFS  (grabbed once)
    ────────────────────────────────────────────────────────── */
    var R = {
      root:          document.getElementById('fm-page'),
      playBtn:       document.getElementById('fm-play-btn'),
      playIcon:      document.getElementById('fm-play-icon'),
      prevBtn:       document.getElementById('fm-prev-btn'),
      nextBtn:       document.getElementById('fm-next-btn'),
      shuffleBtn:    document.getElementById('fm-shuffle-btn'),
      repeatBtn:     document.getElementById('fm-repeat-btn'),
      repeatLabel:   document.getElementById('fm-repeat-label'),
      queueBtn:      document.getElementById('fm-queue-btn'),
      studioBtn:     document.getElementById('fm-studio-btn'),
      studioPanel:   document.getElementById('fm-studio-panel'),
      studioReset:   document.getElementById('fm-studio-reset'),
      studioStatus:  document.getElementById('fm-studio-status'),
      studioDownload:document.getElementById('fm-studio-download'),
      downloadLabel: document.getElementById('fm-download-label'),
      reverbSlider:  document.getElementById('fm-reverb-slider'),
      reverbValue:   document.getElementById('fm-reverb-value'),
      bassSlider:    document.getElementById('fm-bass-slider'),
      bassValue:     document.getElementById('fm-bass-value'),
      speedDownBtn:  document.getElementById('fm-speed-down'),
      speedUpBtn:    document.getElementById('fm-speed-up'),
      speedValue:    document.getElementById('fm-speed-value'),
      pitchDownBtn:  document.getElementById('fm-pitch-down'),
      pitchUpBtn:    document.getElementById('fm-pitch-up'),
      pitchValue:    document.getElementById('fm-pitch-value'),
      presetBtns:    document.querySelectorAll('.fm-preset-btn'),
      npEq:          document.getElementById('fm-np-eq'),
      queuePanel:    document.getElementById('fm-queue-panel'),
      queueList:     document.getElementById('fm-queue-list'),
      queueCount:    document.getElementById('fm-queue-count'),
      npTrack:       document.getElementById('fm-np-track'),
      npArtist:      document.getElementById('fm-np-artist'),
      npEq:          document.getElementById('fm-np-eq'),
      progressTrack: document.getElementById('fm-progress-track'),
      progressFill:  document.getElementById('fm-progress-fill'),
      timeCurrent:   document.getElementById('fm-time-current'),
      timeTotal:     document.getElementById('fm-time-total'),
      volSlider:     document.getElementById('fm-vol-slider'),
      modeBadge:     document.getElementById('fm-mode-badge'),
      liveIndicator: document.getElementById('fm-live-indicator'),
      clockEl:       document.getElementById('fm-clock'),
      embedCode:     document.getElementById('fm-embed-code'),
      copyBtn:       document.getElementById('fm-copy-embed'),
      shareBtn:      document.getElementById('fm-share-btn'),
      storyBtn:      document.getElementById('fm-story-btn'),
      emptyState:    document.getElementById('fm-empty-state'),
      mixesEmpty:    document.getElementById('fm-mixes-empty-state'),
      playerCore:    document.getElementById('fm-player-core'),
      visWrap:       document.getElementById('fm-vis-wrap'),
      sourceSwitch:  document.getElementById('fm-source-switch'),
      srcPlaylistBtn:document.getElementById('fm-src-playlist-btn'),
      srcMixBtn:     document.getElementById('fm-src-mix-btn'),
      searchInput:   document.getElementById('fm-search-input')
    };

    if (!R.root) return; /* Not on FM page */
    window.__lfFmPlayerBound = true;

    state.root = R.root;
    state.bars = R.visWrap ? Array.from(R.visWrap.querySelectorAll('.fm-bar')) : [];

    /* ──────────────────────────────────────────────────────────
       INIT
    ────────────────────────────────────────────────────────── */
    async function init() {
      /* Stream mode runs the whole station off the live stream and has
         no local track list — the playlist/mixes switch doesn't apply. */
      if (mode === 'stream') {
        if (R.sourceSwitch) R.sourceSwitch.style.display = 'none';
        _setModeBadge();
        _initStreamMode();
        _bindControls();
        _startClock();
        _tryAutoplay();
        return;
      }

      /* Local playback: wait for playlist.json + mixes.json to be
         fully fetched and parsed before building anything from them.
         (Checkpoint 20 fix — see file header.) */
      await fmReady();

      _loadSource(state.source, false);
      _bindControls();
      _startClock();

      if (state.isShuffle && R.shuffleBtn) {
        R.shuffleBtn.classList.add('active');
      }

      if (settings.showQueue && R.queuePanel) {
        R.queuePanel.classList.add('open');
        if (R.queueBtn) R.queueBtn.classList.add('open');
      }

      _tryAutoplay();
    }

    /* Autoplay on page load. Browsers will happily allow this if the
       tab/page load itself counts as a permitted context, and block it
       otherwise (Chrome/Safari autoplay policy) — there's no way
       around that from the page's side. So: try immediately, and if
       it's blocked, arm a ONE-TIME listener for the very first
       click/tap/keypress anywhere on the page (not just the Play
       button) that starts playback instantly — meaning in practice
       the station is playing before most visitors do anything else. */
    function _tryAutoplay() {
      if (!state.audio) return;
      var p = state.audio.play();
      if (!p || !p.catch) { state.isPlaying = true; _setPlayingUI(true); _startEqualizer(); return; }
      p.then(function () {
        state.isPlaying = true;
        _setPlayingUI(true);
        _startEqualizer();
        _updateMediaSession();
      }).catch(function () {
        state.isPlaying = false;
        _setPlayingUI(false);
        document.addEventListener('pointerdown', _startOnFirstGesture, { once: true });
        document.addEventListener('keydown', _startOnFirstGesture, { once: true });
      });
    }

    /* BUGFIX — "Play doesn't work the first time." Root cause: when
       browser autoplay is blocked (the normal case), this arms a
       one-time listener on the whole page so ANY first tap/click
       starts playback. But if that first tap happens to land ON the
       Play button, pointerdown fires this listener (which calls
       _play(), flipping isPlaying to true) and THEN the button's own
       'click' handler fires a split second later, sees isPlaying
       already true, and calls _pause() instead — so the tap plays
       for an instant and immediately pauses again. Guard: if the
       gesture landed on the Play button, do nothing here and let the
       button's own click handler (_toggle) start playback exactly
       once, the normal way. Every other tap on the page (anywhere
       else) still unlocks playback immediately as before. */
    function _startOnFirstGesture(e) {
      document.removeEventListener('pointerdown', _startOnFirstGesture);
      document.removeEventListener('keydown', _startOnFirstGesture);
      if (R.playBtn && e && e.target && R.playBtn.contains(e.target)) return;
      if (!state.isPlaying) _play();
    }

    /* ──────────────────────────────────────────────────────────
       SOURCE (Playlist vs Mixes) — swaps the active track list
       without reloading the page. Preserves shuffle setting and
       play/pause intent where the new source has tracks to honor it.
    ────────────────────────────────────────────────────────── */
    function _loadSource(source, preservePlaying) {
      state.source   = source;
      state.playlist = validTracks(source === 'mix' ? window.LF_MIXES : window.LF_PLAYLIST);
      state.currentIdx = 0;

      /* CHECKPOINT 23 (MIXES bug — "when switching to Mixes, playlist
         songs remain"): state.playlist above is already fully replaced
         (not appended to) and _updateQueue() always clears
         R.queueList.innerHTML before rendering, so stale queue rows
         were not actually reproducible in the shipped code. The one
         real residual-state gap: a search query typed under one
         source silently kept filtering the other source's list after
         switching, which could look like "the wrong songs are showing
         up." Cleared here so every source switch starts from a clean,
         fully-visible list. */
      state.searchQuery = '';
      if (R.searchInput) R.searchInput.value = '';

      _setModeBadge();
      _setSourceSwitchUI();

      if (state.playlist.length === 0) {
        if (state.audio) state.audio.pause();
        state.isPlaying = false;
        _setPlayingUI(false);
        _showEmptyState(source);
        _updateQueue();
        return;
      }

      _hideEmptyState();
      _buildPlayOrder();
      _loadTrack(0, !!preservePlaying);
    }

    function _setSourceSwitchUI() {
      if (R.srcPlaylistBtn) {
        R.srcPlaylistBtn.classList.toggle('active', state.source === 'playlist');
        R.srcPlaylistBtn.setAttribute('aria-selected', state.source === 'playlist' ? 'true' : 'false');
      }
      if (R.srcMixBtn) {
        R.srcMixBtn.classList.toggle('active', state.source === 'mix');
        R.srcMixBtn.setAttribute('aria-selected', state.source === 'mix' ? 'true' : 'false');
      }
    }

    /* ──────────────────────────────────────────────────────────
       PLAY ORDER (supports shuffle)
    ────────────────────────────────────────────────────────── */
    function _buildPlayOrder() {
      state.playOrder = state.playlist.map(function (_, i) { return i; });
      if (state.isShuffle) {
        /* Fisher-Yates */
        for (var i = state.playOrder.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = state.playOrder[i];
          state.playOrder[i] = state.playOrder[j];
          state.playOrder[j] = tmp;
        }
      }
    }

    /* ══════════════════════════════════════════════════════════
       STUDIO FX — Web Audio graph
       source (native or SoundTouch) -> fxInput -> bassNode (lowshelf)
         -> [dryGain] -------------------------------\
         \-> [convolver -> wetGain] -> masterGain -> analyser -> destination

       The graph is built LAZILY — only the first time an actual FX
       control is touched (Reverb/Bass slider, Pitch/Speed step btn,
       or a Preset). Plain playback (_play()/_tryAutoplay()) never
       touches Web Audio at all, so ordinary listening is as reliable
       as a plain <audio>.play() call. Opening the Studio panel is a
       pure UI toggle with zero audio side effects. Reverb/Bass/Pitch
       all default to neutral, so the first graph build is inaudible.
       The analyser also feeds the little equalizer visual in the
       mini-player once Studio has been used at least once — until
       then the CSS fmEqPlay animation handles it. The analyser
       exists even for people who never open Studio at all.
    ══════════════════════════════════════════════════════════ */

    function _ensureAudioGraph() {
      var s = state.studio;
      if (s.ctx) {
        // Already built — but make sure it's actually running.
        // AudioContexts created outside a direct user gesture start
        // 'suspended', and this function's callers are always
        // triggered from a real click/slider-drag, so resuming here
        // is always safe and is what makes Studio FX reliably
        // produce sound instead of silently doing nothing.
        if (s.ctx.state === 'suspended') { try { s.ctx.resume(); } catch (e) {} }
        return s.ctx;
      }

      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      s.ctx = new Ctx();

      /* Shared entry point — whichever engine is producing audio
         (native <audio> or a SoundTouch PitchShifter) connects here,
         so swapping engines never has to touch the bass/reverb chain
         itself. */
      s.fxInput = s.ctx.createGain();
      s.fxInput.gain.value = 1;

      s.bassNode = s.ctx.createBiquadFilter();
      s.bassNode.type = 'lowshelf';
      s.bassNode.frequency.value = 150;
      s.bassNode.gain.value = s.bassBoost;

      s.dryGain = s.ctx.createGain();
      s.wetGain = s.ctx.createGain();
      s.dryGain.gain.value = 1 - s.reverb;
      s.wetGain.gain.value = s.reverb;

      s.convolver = s.ctx.createConvolver();
      s.convolver.buffer = _buildImpulseResponse(s.ctx, 2.2, 3.2);

      s.masterGain = s.ctx.createGain();
      s.masterGain.gain.value = state.volume;

      s.analyser = s.ctx.createAnalyser();
      s.analyser.fftSize = 64;
      s.analyser.smoothingTimeConstant = 0.75;
      s.analyserData = new Uint8Array(s.analyser.frequencyBinCount);

      s.fxInput.connect(s.bassNode);
      s.bassNode.connect(s.dryGain);
      s.bassNode.connect(s.convolver);
      s.convolver.connect(s.wetGain);
      s.dryGain.connect(s.masterGain);
      s.wetGain.connect(s.masterGain);
      s.masterGain.connect(s.analyser);
      s.analyser.connect(s.ctx.destination);

      try { s.ctx.resume(); } catch (e) {}

      // Route the native <audio> element through the graph now that
      // the graph actually exists for the first time. This is only
      // ever called from a real FX interaction (Reverb/Bass/Pitch/
      // Speed/Preset handler), never from _play() — so by the time
      // we're here, the AudioContext was created during a genuine
      // user gesture, meaning ctx.resume() will succeed and audio
      // will immediately be audible through the graph.
      _pipeNativeIntoGraph();

      return s.ctx;
    }

    /* Synthesizes a plausible-sounding reverb tail entirely in code —
       exponentially-decaying white noise, the standard cheap way to
       fake a convolution impulse response with no external .wav file
       to host/load. Not studio-grade, but real and controllable. */
    function _buildImpulseResponse(ctx, seconds, decay) {
      var rate = ctx.sampleRate;
      var length = Math.max(1, Math.floor(rate * seconds));
      var impulse = ctx.createBuffer(2, length, rate);
      for (var ch = 0; ch < 2; ch++) {
        var data = impulse.getChannelData(ch);
        for (var i = 0; i < length; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
        }
      }
      return impulse;
    }

    /* Pipes the existing <audio> element into the shared graph tail.
       createMediaElementSource() can only ever be called ONCE per
       element for its entire lifetime (throws on a second call), so
       the resulting node is cached — but ONLY as long as it still
       wraps the CURRENT state.audio element. _loadTrack() creates a
       brand-new `new Audio()` object on every track change, so the
       old cached node (still wrapping the previous, now-abandoned
       element) is stale and needs to be rebuilt for the new element —
       this used to just check "do we have a node at all" and reuse
       the stale one forever after the first track, silently routing
       nothing useful into the graph for every track after the first.

       If the element was already playing when this runs (e.g. Studio
       was just opened while a track was going), reconnecting through
       createMediaElementSource can occasionally leave the element
       paused in some browsers — so playback is explicitly reasserted
       afterward if that happened, instead of leaving the user with
       silence. */
    function _pipeNativeIntoGraph() {
      var s = state.studio;
      var ctx = _ensureAudioGraph();
      if (!ctx || !state.audio) return false;

      var wasPlaying = state.isPlaying && !state.audio.paused;

      if (!s.nativeSrcNode || s.nativeSrcAudioEl !== state.audio) {
        if (s.nativeSrcNode) { try { s.nativeSrcNode.disconnect(); } catch (e) {} }
        try {
          s.nativeSrcNode = ctx.createMediaElementSource(state.audio);
          s.nativeSrcAudioEl = state.audio;
        } catch (e) {
          s.nativeSrcNode = null;
          s.nativeSrcAudioEl = null;
          return false; // already routed elsewhere, or unsupported — caller falls back gracefully
        }
      }
      try { s.nativeSrcNode.disconnect(); } catch (e) {}
      s.nativeSrcNode.connect(s.fxInput);

      // From this instant on, masterGain is the SOLE volume control.
      // The element's own .volume still attenuates the signal before
      // it ever reaches the Web Audio graph, so leaving it at
      // anything but 1 here means every masterGain change gets
      // multiplied by it again (the double-attenuation bug). This is
      // ONLY safe to do when native is actually the audible engine —
      // callers that need the element muted-but-running (SoundTouch
      // swap) set .volume back to 0 themselves right after.
      state.audio.volume = 1;
      _applyNativeRate();

      if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }

      if (wasPlaying && state.audio.paused) {
        var p = state.audio.play();
        if (p && p.catch) p.catch(function () {});
      }

      return true;
    }

    function _setBassGain(db) {
      var s = state.studio;
      s.bassBoost = db;
      if (s.bassNode) s.bassNode.gain.value = db;
    }
    function _setReverbWet(amount) {
      var s = state.studio;
      s.reverb = amount;
      if (s.dryGain)  s.dryGain.gain.value  = 1 - amount;
      if (s.wetGain)  s.wetGain.gain.value  = amount;
    }
    function _setMasterVolume(v) {
      var s = state.studio;
      if (s.masterGain) {
        // Routed through the Web Audio graph — masterGain is the
        // only thing allowed to change. audio.volume must stay
        // pinned at 1, never re-synced to v, or it double-attenuates
        // alongside masterGain.
        s.masterGain.gain.value = v;
      } else if (state.audio) {
        // No graph yet — the plain element is the only thing
        // producing sound, so it's the only thing adjusted here.
        state.audio.volume = v;
      }
    }

    /* Speed, while the NATIVE element is the audible engine, is just
       playbackRate with pitch preservation left on — this is the
       cheap, instant, never-glitches path (this is what most of
       Apple/Spotify's own "speed" controls do under the hood too).
       Vendor-prefixed preservesPitch is set defensively; unsupported
       properties are simply ignored by the JS engine. */
    function _applyNativeRate() {
      if (!state.audio) return;
      state.audio.playbackRate = state.studio.speed;
      state.audio.preservesPitch = true;
      state.audio.mozPreservesPitch = true;
      state.audio.webkitPreservesPitch = true;
    }

    /* Dynamically imports SoundTouch (only the moment Pitch is first
       touched — never on ordinary playback) and caches the module on
       window so a second track/session in the same page load doesn't
       refetch it. */
    function _loadSoundTouchLib() {
      var s = state.studio;
      if (window.__LF_SoundTouch) return Promise.resolve(window.__LF_SoundTouch);
      if (s.stLoadPromise) return s.stLoadPromise;
      s.stLoadPromise = import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/soundtouchjs@0.1.30/+esm')
        .then(function (mod) {
          window.__LF_SoundTouch = mod;
          return mod;
        })
        .catch(function (e) {
          // Rejected dynamic import()s from a browser give almost no
          // detail (just "Failed to fetch dynamically imported
          // module" or similar) — this is nearly always either a
          // Content-Security-Policy script-src block or a genuine
          // network failure reaching the CDN, NOT a code bug. Re-throw
          // with that context attached so the status line actually
          // says which one to check instead of a bare "module error".
          s.stLoadPromise = null; // don't cache a failed import — allow retry next time Pitch is touched
          throw new Error('SoundTouch CDN import failed (CSP or network — check devtools Console/Network tab): ' + (e && e.message ? e.message : e));
        });
      return s.stLoadPromise;
    }

    /* Fetches + decodes the CURRENT track's audio data exactly once,
       caching the resulting AudioBuffer by src. Every subsequent
       Pitch tweak on the same track reuses this — the fix for
       "SoundTouch re-decoding on every slider tick." */
    function _getDecodedBuffer(track) {
      var s = state.studio;
      if (s.bufferCache[track.src]) return s.bufferCache[track.src];
      var p = fetch(track.src)
        .then(function (r) {
          if (!r.ok) throw new Error('Track fetch failed: HTTP ' + r.status);
          return r.arrayBuffer();
        }, function (networkErr) {
          // fetch() itself rejecting (not just a non-OK response) is
          // almost always a CORS block on the track's host, not a
          // dead link — surfaced explicitly so it's not confused with
          // the SoundTouch CDN failure above.
          throw new Error('Track fetch blocked (likely CORS on the file host): ' + (networkErr && networkErr.message ? networkErr.message : networkErr));
        })
        .then(function (ab) {
          // Decoding needs its own short-lived context — decodeAudioData
          // works fine off the main s.ctx and this avoids any chance of
          // disturbing whatever's currently connected/playing on it.
          var DecodeCtx = window.AudioContext || window.webkitAudioContext;
          var tmpCtx = new DecodeCtx();
          return tmpCtx.decodeAudioData(ab).then(function (buf) {
            tmpCtx.close();
            return buf;
          }, function (decodeErr) {
            tmpCtx.close();
            throw new Error('Audio decode failed (file may not be a clean MP3/AAC stream): ' + (decodeErr && decodeErr.message ? decodeErr.message : decodeErr));
          });
        });
      s.bufferCache[track.src] = p;
      p.catch(function () { delete s.bufferCache[track.src]; }); // don't cache a failure
      return p;
    }

    /* BUGFIX — pitch swap "glitch", rewritten. The previous version of
       this helper scheduled the volume dip/restore as AudioParam
       automation tied to ctx.currentTime, and deferred the actual
       swap to a setTimeout matched to that automation's duration.
       That's exactly the kind of thing that gets stuck: if the
       AudioContext is ever suspended at that moment (very common —
       browsers suspend contexts routinely), ctx.currentTime simply
       stops advancing, the scheduled ramp-back-up never plays out,
       and fxInput is left sitting near-silent — a PERSISTENT mute,
       not a glitch, which matches what was actually being reported.
       Fixed properly this time:
         1. Resume the context FIRST, synchronously, before scheduling
            anything on it.
         2. The dip down is a direct, immediate .value assignment —
            not automation — so it can never be "stuck waiting" on
            anything.
         3. The actual swap runs synchronously, right here, not
            deferred — nothing can get lost to a delayed callback.
         4. Only the restore back up to full volume uses a short ramp
            (now that the context is confirmed running), just to
            avoid an audible click on the way back in. */
    function _declickSwap(doSwap) {
      var s = state.studio;
      var ctx = s.ctx;
      if (!ctx || !s.fxInput) { doSwap(); return; }

      if (ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }

      try {
        s.fxInput.gain.cancelScheduledValues(ctx.currentTime);
        s.fxInput.gain.value = 0.0001;
      } catch (e) {}

      /* Capture node refs before the swap — doSwap could theoretically
         null s.fxInput (nothing currently does, but be safe). */
      var _rampCtx = ctx;
      var _rampFx  = s.fxInput;

      /* THE CORE FIX — doSwap() was previously a bare call. If it
         throws (which _engageSoundTouch's build path does via
         `throw buildErr` on any construction error), the entire
         gain-restore block that follows was simply never reached —
         fxInput was left permanently at 0.0001 (= total silence).
         No amount of clicking Play could recover it. Wrapping in
         try/catch means the restore below runs regardless. */
      var _swapErr = null;
      try { doSwap(); } catch (e) { _swapErr = e; }

      /* ALWAYS restore gain — two-layer guarantee:
         Layer 1 — 40ms ramp: smooth fade avoids an audible click on
           the way back in. If the context is running this fires
           immediately and finishes ~40ms later.
         Layer 2 — setTimeout safety net: if the AudioContext is
           suspended at the moment of scheduling, the ramp is deferred
           indefinitely by the browser. The safety net fires at 200ms
           and force-sets gain=1, which takes effect the instant the
           context resumes (or immediately if it's already running).
           200ms >> 40ms ramp, so it never races the ramp on a healthy
           context — it only matters when the ramp is stuck waiting. */
      try {
        var now = _rampCtx.currentTime;
        _rampFx.gain.cancelScheduledValues(now);
        _rampFx.gain.setValueAtTime(0.0001, now);
        _rampFx.gain.linearRampToValueAtTime(1, now + 0.04);
      } catch (e) {
        /* Ramp scheduling itself failed — restore immediately. */
        try { _rampFx.gain.value = 1; } catch (e2) {}
      }
      setTimeout(function () {
        try {
          if (_rampCtx.state !== 'closed') {
            _rampFx.gain.cancelScheduledValues(_rampCtx.currentTime);
            _rampFx.gain.value = 1;
          }
        } catch (e) {}
      }, 200);

      /* Re-throw so callers (.catch in _engageSoundTouch, etc.) still
         receive the failure and can handle it at their level. */
      if (_swapErr) throw _swapErr;
    }

    /* Switches the audible engine to SoundTouch for real independent
       Pitch. Never pauses anything: the native element keeps playing
       (just muted, once the shifter is actually ready) so its
       timeupdate/ended events keep driving the UI throughout, and the
       fetch+decode+build all happen in the background while whatever
       was audible before keeps being audible. */
    function _engageSoundTouch() {
      var s = state.studio;
      var ctx = _ensureAudioGraph();
      var track = _currentTrack();
      if (!ctx || !track || !state.audio) return;

      var token = ++s.swapToken;
      _setStudioStatus('Loading pitch engine…', 'is-loading');

      /* If the SoundTouch CDN or track fetch completely hangs (no
         response at all), the "Loading…" state would be permanent —
         Promise.race() caps it at 9s, after which the catch branch
         runs exactly as it would for any other error, leaving the
         user on standard playback with a clear fallback message.
         The catch handles both timeout and real network errors the
         same way, so no special casing needed. */
      var _stTimeout = new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('SoundTouch load timeout')); }, 9000);
      });

      /* BUGFIX — foreground-return rebuild produced a broken shifter
         (pitchSemitones read back undefined, signal died within a
         second). Root cause: _ensureAudioGraph() above calls
         ctx.resume() but never waits for it — resume() is a real
         async operation, slower than a microtask, so right after a
         background→foreground visibilitychange the context can still
         report 'suspended' for a beat. _getDecodedBuffer()/
         _loadSoundTouchLib() are both cache-hits in that scenario and
         resolve almost immediately, which meant the PitchShifter used
         to get constructed (and connected) against a context that
         hadn't actually finished resuming yet — it allocates its own
         audio nodes against ctx internally, and building those against
         a not-yet-running context is what left the object partially
         wired (looks like a shifter, s.shifter gets assigned, but
         never fully initializes). _resumeCtx() waits for the real
         'running' state — capped, so a context that never resumes
         can't hang Studio forever — before anything gets built. */
      function _resumeCtx() {
        if (ctx.state === 'running') return Promise.resolve();
        var resumed;
        try { resumed = ctx.resume(); } catch (e) { resumed = null; }
        if (!resumed || !resumed.then) return new Promise(function (res) { setTimeout(res, 50); });
        return Promise.race([
          resumed.catch(function () {}),
          new Promise(function (res) { setTimeout(res, 600); })
        ]);
      }

      Promise.race([
        Promise.all([_resumeCtx(), _loadSoundTouchLib(), _getDecodedBuffer(track)]),
        _stTimeout
      ])
        .then(function (results) {
          if (token !== s.swapToken) return; // superseded — pitch/track changed again before this resolved
          var STModule = results[1], buffer = results[2];

          // Validate the inputs BEFORE building anything against them.
          // A stale/failed cache entry or an unexpected module shape
          // should fall straight to the catch() fallback below instead
          // of being fed into `new PitchShifter(...)` as if it were
          // good data — that silent-garbage-in path is exactly what
          // "the fetch/decode failing silently and still getting
          // treated as a valid shifter" describes.
          if (!STModule || typeof STModule.PitchShifter !== 'function') {
            throw new Error('SoundTouch module unavailable');
          }
          if (!buffer || typeof buffer.duration !== 'number' || buffer.duration <= 0) {
            throw new Error('Decoded audio buffer invalid');
          }

          var builtShifter = null;

          _declickSwap(function () {
            // Tear down any previous shifter cleanly (deferred .off()
            // so it never blocks the audio thread — same pattern as
            // _engageNative uses).
            if (s.shifter) {
              var oldShifter = s.shifter;
              s.shifter = null;
              try { oldShifter.disconnect(); } catch (e) {}
              setTimeout(function () { try { oldShifter.off(); } catch (e) {} }, 0);
            }

            // Snapshot native position RIGHT NOW — this is the
            // reference point we'll seek both the shifter and native
            // to, so they start from exactly the same moment with
            // zero drift window.
            var snapFrac = (state.audio.duration && state.audio.duration > 0)
              ? Math.max(0, Math.min(1, state.audio.currentTime / state.audio.duration))
              : 0;

            // BUGFIX (root cause, confirmed via the exact error text
            // "Pitch shifter did not finish initializing") — the old
            // check here read `shifter.pitchSemitones` straight back
            // after setting it, to confirm the assignment "stuck."
            // But this SoundTouch build's `pitchSemitones` is a
            // WRITE-ONLY property — it has a setter, but no matching
            // getter, so reading it back is `undefined` by normal JS
            // accessor semantics regardless of whether the pitch shift
            // is actually working. That made this check fail 100% of
            // the time, on every browser, every attempt — which is
            // exactly the symptom reported (never intermittent,
            // always "unavailable"). It was a false alarm tearing
            // down a shifter that had, in every case, actually built
            // successfully. Fixed by validating shifter SHAPE instead
            // — the methods it must have to be usable — rather than
            // reading back a property that was never meant to be read.
            try {
              var shifter = new STModule.PitchShifter(ctx, buffer, 4096);
              shifter.tempo = s.speed;
              shifter.pitchSemitones = s.pitch;

              if (!shifter || typeof shifter.connect !== 'function' || typeof shifter.disconnect !== 'function') {
                throw new Error('Pitch shifter object malformed after construction');
              }

              // Seek shifter to the snapshot position. The library's
              // setter takes a 0..1 fraction (its getter reports 0..100
              // — known asymmetry in the library itself, not a typo
              // here).
              try { shifter.percentagePlayed = snapFrac; } catch (e) {}

              // Cut native out of the audible path BEFORE connecting the
              // shifter. Native keeps playing silently so timeupdate/
              // ended keep driving the UI.
              if (s.nativeSrcNode) { try { s.nativeSrcNode.disconnect(s.fxInput); } catch (e) {} }
              state.audio.volume = 0;

              // BUGFIX (root cause of "reset to 0 mutes/breaks" once
              // Pitch is actually engaging successfully) — this used
              // to force-write state.audio.currentTime = snapFrac *
              // duration right here, "so handback returns to the
              // correct spot." But snapFrac was computed a moment ago
              // FROM state.audio.currentTime — native is already
              // sitting at that exact position. Re-assigning
              // currentTime to (effectively) its own current value
              // still counts as a seek to the browser, which can force
              // an unnecessary re-buffer/network request on the
              // element right as it's competing for bandwidth with the
              // fetch+decode that just ran to build this shifter —
              // exactly the same "silently stalls, .paused stays
              // false" failure mode documented in _engageNative below,
              // just triggered from the other side of the swap. Native
              // keeps playing (muted) uninterrupted for the entire
              // time Pitch is active, so its currentTime tracks
              // correctly on its own with zero intervention needed —
              // this write was solving a problem that didn't exist
              // while creating a real one. Removed outright rather
              // than guarded, since it was never actually necessary.

              // Only bring it in audibly if playback is actually
              // supposed to be running right now — otherwise a Pitch
              // change made while paused would start the shifter
              // playing on its own, overriding the pause. _play() (see
              // above) connects it later when the user actually hits
              // Play.
              if (state.isPlaying) { shifter.connect(s.fxInput); }

              s.shifter = shifter;
              builtShifter = shifter;
            } catch (buildErr) {
              // Roll back to native audible immediately — don't leave
              // the graph half-swapped (native disconnected, nothing
              // valid connected in its place).
              if (s.nativeSrcNode && s.fxInput) { try { s.nativeSrcNode.connect(s.fxInput); } catch (e) {} }
              if (state.audio) state.audio.volume = 1;
              throw buildErr;
            }
          });

          if (!builtShifter) throw new Error('Pitch shifter build did not complete');

          s.engine = 'soundtouch';
          _setStudioStatus('Pitch engine active', 'is-active');
          _syncStudioUI();
        })
        .catch(function (err) {
          if (token !== s.swapToken) return;
          console.warn('[LFJ FM Studio] SoundTouch unavailable — staying on standard playback', err);
          s.shifter = null;
          s.engine = 'native';
          /* DIAGNOSTIC — the exact reason is now visible on-screen (in
             the status line, not just devtools console) via
             _lastPitchError below, so the next failure is provable
             instead of guessed at. Kept short since it renders in the
             UI status area. */
          s.lastPitchError = (err && (err.message || String(err))) || 'unknown error';
          console.warn('[LFJ FM Studio] Pitch engine failure reason: ' + s.lastPitchError);
          /* Make sure native is definitely the audible path. */
          if (s.nativeSrcNode && s.fxInput) { try { s.nativeSrcNode.connect(s.fxInput); } catch (e) {} }
          if (state.audio) state.audio.volume = 1;
          /* Belt-and-suspenders gain restore. _declickSwap now always
             restores gain even when doSwap throws (the main fix). But
             errors thrown BEFORE _declickSwap runs — the 9s timeout,
             and the module/buffer validation throws — never dip fxInput
             in the first place, so gain is already fine in those paths.
             Errors thrown INSIDE doSwap are handled by _declickSwap.
             This line is a catch-all so any future path reaching here
             can never leave fxInput muted. */
          if (s.fxInput) {
            try {
              if (s.ctx && s.ctx.state !== 'closed') {
                s.fxInput.gain.cancelScheduledValues(s.ctx.currentTime);
              }
              s.fxInput.gain.value = 1;
            } catch (e) {}
          }
          _setStudioStatus('Pitch shift unavailable (' + s.lastPitchError + ') — Speed still works', 'is-fallback');
          _syncStudioUI();
        });
    }

    /* Switches back to the cheap native path — used whenever Pitch
       returns to 0. Native has been playing (muted) the whole time
       SoundTouch was active, so this is just a reconnect + unmute,
       never a re-load. */
    function _engageNative() {
      var s = state.studio;
      ++s.swapToken; // invalidate any in-flight SoundTouch build

      // Snapshot playing intent BEFORE touching the graph — this is
      // what gets checked against the ACTUAL element state after the
      // reconnect, not just assumed.
      var shouldBePlaying = state.isPlaying;

      _declickSwap(function () {
        if (s.shifter) {
          var dyingShifter = s.shifter;
          s.shifter = null;

          // BUGFIX — this used to try to sync native's playhead here
          // by reading dyingShifter.percentagePlayed back as a 0..100
          // value. That doesn't work: in this SoundTouch build,
          // percentagePlayed is only ever delivered through the
          // shifter's 'play' event payload (detail.percentagePlayed),
          // not as a property you can read directly off the shifter —
          // same write-only-style trap as pitchSemitones. The read
          // was silently NaN/undefined every time, which — combined
          // with removing the matching force-seek on the engage side
          // above — means native has simply been left alone this
          // whole time. That's actually correct: native has been
          // playing (muted) continuously and without interruption
          // since Pitch engaged, so its own currentTime already
          // tracks the right position with zero intervention needed.
          // No seek, no buffered-range check, no risk of forcing a
          // stall right at handback — the exact spot the original
          // "reset to 0 mutes it" bug came from.

          // Disconnect first (cuts the ScriptProcessor out of the audio
          // graph immediately — no more audio-thread work from it).
          // Then defer .off() to the next tick so the audio thread has
          // already moved on before we drain the SP buffer, preventing
          // the UI freeze that synchronous .off() causes.
          try { dyingShifter.disconnect(); } catch (e) {}
          setTimeout(function () { try { dyingShifter.off(); } catch (e) {} }, 0);
        }

        if (s.nativeSrcNode && s.fxInput) {
          try { s.nativeSrcNode.connect(s.fxInput); } catch (e) {} // no-op if already connected
        }
        if (state.audio) state.audio.volume = 1;
        _applyNativeRate();

        // BUGFIX — "pitch back to 0 pauses the music." The
        // MediaElementSource reconnect above can occasionally leave
        // the <audio> element itself paused in some browsers (the
        // same quirk already documented and guarded against on
        // _pipeNativeIntoGraph — this handback path does the same
        // reconnect but was missing the same guard). When that
        // happens, Studio still thinks it's playing (isPlaying stays
        // true) but no sound comes out, which reads exactly like a
        // pause+glitch the moment Pitch returns to neutral. Catch and
        // correct it immediately instead of leaving it silent.
        if (shouldBePlaying && state.audio && state.audio.paused) {
          var p = state.audio.play();
          if (p && p.catch) p.catch(function () {});
        }
      });

      s.engine = 'native';
      _setStudioStatus('Standard playback engine');

      /* Diagnostic — console only (was overwriting studioStatus text
         1s after _setStudioStatus ran, making the UI look broken even
         when audio was fine). Open devtools to see the signal report. */
      (function () {
        var s2 = state.studio;
        function snapshot() {
          var rawSignal = '?';
          if (s2.analyser && s2.analyserData) {
            try {
              s2.analyser.getByteFrequencyData(s2.analyserData);
              var sum = 0;
              for (var i = 0; i < s2.analyserData.length; i++) sum += s2.analyserData[i];
              rawSignal = (sum / s2.analyserData.length).toFixed(1);
            } catch (e) {}
          }
          var bufferedStr = '?', inBuffered = '?';
          if (state.audio) {
            try {
              var buf = state.audio.buffered;
              var parts = [];
              var ct = state.audio.currentTime;
              var hit = false;
              for (var b = 0; b < buf.length; b++) {
                parts.push(buf.start(b).toFixed(1) + '-' + buf.end(b).toFixed(1));
                if (ct >= buf.start(b) && ct <= buf.end(b)) hit = true;
              }
              bufferedStr = parts.join(',') || 'none';
              inBuffered = hit;
            } catch (e) {}
          }
          return 'ctx=' + (s2.ctx && s2.ctx.state) +
            ' fxGain=' + (s2.fxInput && s2.fxInput.gain.value != null ? s2.fxInput.gain.value.toFixed(4) : '?') +
            ' aVol=' + (state.audio && state.audio.volume) +
            ' aPaused=' + (state.audio && state.audio.paused) +
            ' RAWSIGNAL=' + rawSignal +
            ' readyState=' + (state.audio && state.audio.readyState) +
            ' networkState=' + (state.audio && state.audio.networkState) +
            ' curTime=' + (state.audio && state.audio.currentTime != null ? state.audio.currentTime.toFixed(1) : '?') +
            ' buffered=[' + bufferedStr + ']' +
            ' inBuffered=' + inBuffered;
        }
        var immediate = snapshot();
        setTimeout(function () {
          var settled = snapshot();
          console.log('[LFJ FM Studio] _engageNative — t0: ' + immediate + ' || +1s: ' + settled);
        }, 1000);
      })();

      // HARD SAFETY NET — this is the one guarantee that matters most:
      // reset-to-0 must never leave the music silent, full stop, no
      // matter what caused it. Everything above this point fixes the
      // actual root causes found so far, but this runs regardless of
      // whether some other, still-undiscovered path manages to leave
      // things half-wired. 300ms after the swap — comfortably past the
      // declick ramp and the earlier 200ms gain safety net — re-check
      // the three things that must be true for sound to actually come
      // out (fxInput gain open, native unmuted, native not paused) and
      // force every one of them if it's playing but any is off.
      setTimeout(function () {
        if (!shouldBePlaying || !state.audio) return;
        var s3 = state.studio;
        if (s3.fxInput && s3.fxInput.gain.value < 0.9) {
          try { s3.fxInput.gain.cancelScheduledValues(s3.ctx.currentTime); } catch (e) {}
          try { s3.fxInput.gain.setValueAtTime(1, s3.ctx.currentTime); } catch (e) {}
        }
        if (state.audio.volume < 1) state.audio.volume = 1;
        if (state.audio.paused) {
          var p2 = state.audio.play();
          if (p2 && p2.catch) p2.catch(function () {});
        }
      }, 300);
    }

    /* Pitch step buttons — moves one semitone at a time through
       PITCH_STEPS. Only pitch!=0 ever needs SoundTouch; Speed alone
       never does. */
    function _stepPitch(dir) {
      var s = state.studio;
      var i = PITCH_STEPS.indexOf(s.pitch);
      if (i === -1) i = PITCH_STEPS.indexOf(0);
      i = Math.max(0, Math.min(PITCH_STEPS.length - 1, i + dir));
      s.pitch = PITCH_STEPS[i];

      if (s.pitch === 0) {
        _engageNative();
      } else if (s.engine === 'soundtouch' && s.shifter) {
        // Already running SoundTouch for this track — just nudge the
        // property. No rebuild, no re-decode.
        s.shifter.pitchSemitones = s.pitch;
      } else {
        _ensureAudioGraph();
        _engageSoundTouch();
      }
      _syncStudioUI();

      /* Diagnostic — console only. Reports the intended value and
         engine state, not a readback of shifter.pitchSemitones — that
         property is write-only in this SoundTouch build (see the
         BUGFIX note in _engageSoundTouch), so reading it back always
         showed as unset even when pitch was applied correctly. That
         false signal is exactly what sent earlier debugging sessions
         chasing a "broken shifter" that wasn't actually broken. */
      setTimeout(function () {
        console.log('[LFJ FM Studio] _stepPitch — desired=' + s.pitch +
          ' shifterExists=' + (s.shifter ? 'yes (connect=' + (typeof s.shifter.connect === 'function') + ')' : 'no') +
          ' engine=' + s.engine + ' swapToken=' + s.swapToken);
      }, 50);
    }

    /* Speed step buttons — moves through SPEED_STEPS. Updates whichever
       engine is currently live in place, no rebuild either way. */
    function _stepSpeed(dir) {
      var s = state.studio;
      var i = SPEED_STEPS.indexOf(s.speed);
      if (i === -1) i = SPEED_STEPS.indexOf(1);
      i = Math.max(0, Math.min(SPEED_STEPS.length - 1, i + dir));
      s.speed = SPEED_STEPS[i];

      if (s.engine === 'soundtouch' && s.shifter) {
        s.shifter.tempo = s.speed;
      }
      // Native keeps its own rate matched to Speed regardless of which
      // engine is audible right now — it's the thing driving progress/
      // ended events, and the thing SoundTouch hands back to later, so
      // it must never drift out of step with the intended tempo.
      _applyNativeRate();
      _syncStudioUI();
    }

    /* ── Studio FX prefs: saved, but never restored, on purpose ──
       Every fresh page visit starts clean at defaults (Pitch 0, Speed
       1x, Reverb 0, Bass 0) — no carryover, no exceptions. This also
       resolves the inconsistency where Speed used to hang around but
       Pitch didn't: neither does now, both always come back to 0/1x.
       _saveStudioPrefs() below is a harmless write-only leftover from
       an earlier attempt at cross-visit persistence — nothing ever
       reads LF_STUDIO_KEY back, so it has zero effect on behavior.
       Left in rather than touching its 8 call sites, since it's inert. */
    var LF_STUDIO_KEY = 'lf_fm_studio_v1';
    function _saveStudioPrefs() {
      try {
        var s = state.studio;
        localStorage.setItem(LF_STUDIO_KEY, JSON.stringify({
          reverb: s.reverb, bassBoost: s.bassBoost, pitch: s.pitch, speed: s.speed
        }));
      } catch (e) {}
    }

    /* One-tap FX presets — sets Speed/Pitch/Reverb/Bass together, then
       reuses the exact same engine-switch logic as the individual
       steppers/sliders, so a preset can never leave Studio in a state
       the manual controls couldn't also reach. */
    function _applyPreset(name) {
      var p = FX_PRESETS[name];
      if (!p) return;
      var s = state.studio;
      _ensureAudioGraph();
      s.speed = p.speed;
      s.reverb = p.reverb;
      s.bassBoost = p.bass;
      _setReverbWet(p.reverb);
      _setBassGain(p.bass);
      if (p.pitch === 0) {
        s.pitch = 0;
        _engageNative();
      } else {
        s.pitch = p.pitch;
        _applyNativeRate();
        _engageSoundTouch();
      }
      _syncStudioUI();
      _saveStudioPrefs();
      if (R.presetBtns) {
        R.presetBtns.forEach(function (btn) {
          btn.classList.toggle('active', btn.getAttribute('data-preset') === name);
        });
      }
    }

    /* Called from _loadTrack on EVERY track change — re-applies
       whatever Studio settings are currently dialed in to the new
       track. This is the "effects stay the same when the song
       switches" requirement: state.studio.* is never reset here,
       only re-applied. Only touches the Web Audio graph if it
       already exists (i.e. Studio has actually been opened/used this
       session) — ordinary playback never creates it, so it behaves
       exactly like the corner ambient button and the FM mini-widget
       until Studio is actually engaged.

       A SoundTouch shifter is built against ONE specific track's
       decoded buffer, so it can't just carry over to a new track —
       if Pitch is currently non-zero, the native element is put back
       in the audible path (instantly, at the correct Speed) so the
       new track is never silent, while a fresh shifter for the new
       track spins up in the background and swaps in the moment it's
       ready — same never-pause/never-gap approach as the initial
       engage. */
    function _applyStudioToNewTrack() {
      var s = state.studio;
      if (!s.ctx) return; // Studio never opened this session
      if (s.shifter) {
        var oldShifter = s.shifter;
        s.shifter = null;
        try { oldShifter.disconnect(); } catch (e) {}
        setTimeout(function () { try { oldShifter.off(); } catch (e) {} }, 0);
      }
      s.engine = 'native';
      _pipeNativeIntoGraph(); // native audible immediately at current Speed
      if (s.pitch !== 0) _engageSoundTouch(); // then upgrade to Pitch in the background
    }

    /* Reflects current Studio state into the panel — slider fill,
       value labels, per-row glow state, and the Studio button's own
       glow (lit the instant ANY control is off its default). */
    function _syncStudioUI() {
      var s = state.studio;
      if (R.reverbSlider) R.reverbSlider.value = Math.round(s.reverb * 100);
      if (R.reverbValue)  R.reverbValue.textContent = Math.round(s.reverb * 100) + '%';
      if (R.bassSlider)   R.bassSlider.value   = s.bassBoost;
      if (R.bassValue)    R.bassValue.textContent = s.bassBoost + ' dB';
      if (R.speedValue)   R.speedValue.textContent = (s.speed % 1 === 0 ? s.speed + '.0' : s.speed) + 'x';
      if (R.pitchValue)   R.pitchValue.textContent = s.pitch > 0 ? '+' + s.pitch : String(s.pitch);

      _setRowEngaged(R.reverbSlider, s.reverb > 0);
      _setRowEngaged(R.bassSlider,   s.bassBoost > 0);
      _setRowEngaged(R.speedValue,   s.speed !== 1);
      _setRowEngaged(R.pitchValue,   s.pitch !== 0);

      if (R.speedDownBtn) R.speedDownBtn.disabled = (s.speed === SPEED_STEPS[0]);
      if (R.speedUpBtn)   R.speedUpBtn.disabled   = (s.speed === SPEED_STEPS[SPEED_STEPS.length - 1]);
      if (R.pitchDownBtn) R.pitchDownBtn.disabled = (s.pitch === PITCH_STEPS[0]);
      if (R.pitchUpBtn)   R.pitchUpBtn.disabled   = (s.pitch === PITCH_STEPS[PITCH_STEPS.length - 1]);

      var anyEngaged = s.reverb > 0 || s.bassBoost > 0 || s.pitch !== 0 || s.speed !== 1;
      if (R.studioBtn) R.studioBtn.classList.toggle('active', anyEngaged);
    }
    function _setRowEngaged(el, engaged) {
      if (!el) return;
      var row = el.closest('.fm-studio-row');
      if (row) row.setAttribute('data-engaged', engaged ? 'true' : 'false');
    }

    /* ── DOWNLOAD WITH FX ──────────────────────────────────────
       Bounces the CURRENT track, with whatever Reverb/Bass are
       currently dialed in, to a real downloadable .wav file via an
       OfflineAudioContext render — not just a copy of the original.
       Falls back to a plain, un-effected copy (with a clear status
       message) if the offline render fails for any reason — a broken
       bounce should never look like "the download button doesn't
       work". */

    /* is-loading gets an animated dot — a small, obviously-intentional
       "working on it" cue instead of text just changing with no
       explanation, which is what read as buggy before. */
    function _setStudioStatus(msg, kind) {
      if (!R.studioStatus) return;
      R.studioStatus.className = 'fm-studio-status' + (kind ? ' ' + kind : '');
      if (kind === 'is-loading') {
        R.studioStatus.innerHTML = '<span class="fm-status-dot" aria-hidden="true"></span>' + msg;
      } else {
        R.studioStatus.textContent = msg;
      }
    }

    /* ── DOWNLOAD WITH FX ──────────────────────────────────────
       Bounces the CURRENT track, with whatever Reverb/Bass are
       currently dialed in, to a real downloadable .wav file via an
       OfflineAudioContext render — not just a copy of the original.
       Falls back to a plain, un-effected copy (with a clear status
       message) if the offline render fails for any reason — a broken
       bounce should never look like "the download button doesn't
       work". */
    function _downloadWithFX() {
      var track = _currentTrack();
      if (!track || !track.src) return;
      var s = state.studio;
      var btn = R.studioDownload, label = R.downloadLabel;
      if (btn) btn.disabled = true;
      if (label) label.textContent = 'Rendering…';

      // Opened HERE, synchronously, inside the click handler — this is
      // the only way a new tab survives popup blockers. Everything
      // else about the download happens inside THIS tab reference,
      // never the current page, so the current tab never navigates
      // or flickers.
      var destTab = null;
      try { destTab = window.open('', '_blank'); } catch (e) {}
      if (destTab) {
        try {
          destTab.document.title = 'Rendering — LIVEFASTJAYDO FM';
          destTab.document.body.style.cssText = 'margin:0;background:#0a0a0a;color:#00ff88;font:13px monospace;display:flex;align-items:center;justify-content:center;height:100vh;letter-spacing:.08em;text-transform:uppercase;';
          destTab.document.body.textContent = 'Rendering your download…';
        } catch (e) {}
      }

      var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!OfflineCtx) { _downloadFail(btn, label, destTab, 'Offline rendering not supported in this browser'); return; }

      /* Reuses the already-decoded buffer if Pitch has touched this
         track this session (same cache _engageSoundTouch fills), so a
         download right after using Pitch doesn't re-fetch. */
      _getDecodedBuffer(track).then(function (buffer) {
          var speed = s.speed || 1;
          var outLength = Math.ceil(buffer.length / speed);
          var offline = new OfflineCtx(buffer.numberOfChannels, outLength, buffer.sampleRate);

          var bass = offline.createBiquadFilter();
          bass.type = 'lowshelf'; bass.frequency.value = 150; bass.gain.value = s.bassBoost;
          var dry = offline.createGain(); dry.gain.value = 1 - s.reverb;
          var wet = offline.createGain(); wet.gain.value = s.reverb;
          var convolver = offline.createConvolver();
          convolver.buffer = _buildImpulseResponse(offline, 2.2, 3.2);
          bass.connect(dry); bass.connect(convolver); convolver.connect(wet);
          dry.connect(offline.destination); wet.connect(offline.destination);

          var src = offline.createBufferSource();
          src.buffer = buffer;
          src.playbackRate.value = speed; // Speed carries into the bounce; true independent Pitch (SoundTouch) does not — see status note below
          src.connect(bass);
          src.start(0);

          return offline.startRendering();
        })
        .then(function (rendered) {
          _finishDownload(rendered, track, btn, label, destTab, s.pitch !== 0);
        })
        .catch(function (err) {
          console.warn('[FM Download]', err);
          _downloadFail(btn, label, destTab, 'Could not load this track for download (CORS or network)');
        });
    }

    function _finishDownload(renderedBuffer, track, btn, label, destTab, pitchWasSkipped) {
      var wavBlob = _bufferToWav(renderedBuffer);
      var url = URL.createObjectURL(wavBlob);
      var filename = (track.title || 'track').replace(/[^a-z0-9 \-]/gi, '').trim() + ' (FX).wav';

      /* The download link lives entirely INSIDE the already-open
         destination tab — the current page is never touched, never
         navigated, never flickers. */
      if (destTab && !destTab.closed) {
        try {
          destTab.document.title = filename;
          var a = destTab.document.createElement('a');
          a.href = url; a.download = filename;
          destTab.document.body.appendChild(a);
          a.click();
          destTab.document.body.textContent = '';
          var msg = destTab.document.createElement('div');
          msg.textContent = 'Downloaded — ' + filename;
          destTab.document.body.appendChild(msg);
        } catch (e) {
          try { destTab.location.href = url; } catch (e2) {}
        }
      } else {
        _setStudioStatus('Allow pop-ups to receive your download in a new tab', 'is-fallback');
      }

      setTimeout(function () { URL.revokeObjectURL(url); }, 30000);
      if (btn) btn.disabled = false;
      if (label) label.textContent = 'Downloaded ✓';
      if (pitchWasSkipped) _setStudioStatus('Downloaded with Bass/Reverb/Speed — Pitch shift isn\u2019t included in downloads yet', 'is-fallback');
      setTimeout(function () { if (label) label.textContent = 'Download With FX'; }, 2500);
    }
    function _downloadFail(btn, label, destTab, msg) {
      _setStudioStatus(msg, 'is-fallback');
      if (destTab && !destTab.closed) { try { destTab.close(); } catch (e) {} }
      if (btn) btn.disabled = false;
      if (label) label.textContent = 'Download With FX';
    }

    /* Encodes an AudioBuffer to a 16-bit PCM WAV Blob — hand-written,
       zero dependencies, works everywhere. Interleaves channels, then
       writes a standard 44-byte RIFF/WAVE header in front of the raw
       samples. */
    function _bufferToWav(buffer) {
      var numCh = buffer.numberOfChannels;
      var len = buffer.length * numCh * 2 + 44;
      var out = new ArrayBuffer(len);
      var view = new DataView(out);
      var sampleRate = buffer.sampleRate;

      function writeStr(offset, str) { for (var i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); }

      writeStr(0, 'RIFF');
      view.setUint32(4, len - 8, true);
      writeStr(8, 'WAVE');
      writeStr(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, numCh, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numCh * 2, true);
      view.setUint16(32, numCh * 2, true);
      view.setUint16(34, 16, true);
      writeStr(36, 'data');
      view.setUint32(40, len - 44, true);

      var channels = [];
      for (var c = 0; c < numCh; c++) channels.push(buffer.getChannelData(c));

      var offset = 44;
      for (var i = 0; i < buffer.length; i++) {
        for (var ch = 0; ch < numCh; ch++) {
          var sample = Math.max(-1, Math.min(1, channels[ch][i]));
          view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
          offset += 2;
        }
      }
      return new Blob([out], { type: 'audio/wav' });
    }

    /* ──────────────────────────────────────────────────────────
       LOAD TRACK
    ────────────────────────────────────────────────────────── */
    function _loadTrack(orderIdx, andPlay) {
      if (state.playlist.length === 0) return;
      orderIdx = ((orderIdx % state.playlist.length) + state.playlist.length) % state.playlist.length;
      state.currentIdx = orderIdx;

      var trackIdx  = state.playOrder[orderIdx];
      var track     = state.playlist[trackIdx];
      if (!track) return;

      /* Create / reuse audio element */
      if (!state.audio) {
        state.audio         = new Audio();
        state.audio.preload = 'metadata';
        state.audio.volume  = state.volume;
        _bindAudioEvents();
      }

      state.loadToken++;
      state.audio.src = track.src;
      state.audio.load();

      /* Re-apply whatever Studio FX are currently dialed in to this
         new track (never reset here — see state.studio comment for
         why that's the whole point). */
      _applyStudioToNewTrack();

      /* Update UI */
      _updateNowPlaying(track);
      _updateQueue();
      _resetProgress();
      _updateMediaSession();

      if (andPlay) {
        _play();
      }
    }

    /* ──────────────────────────────────────────────────────────
       STREAM MODE INIT
    ────────────────────────────────────────────────────────── */
    function _initStreamMode() {
      if (!state.audio) {
        state.audio        = new Audio();
        state.audio.volume = state.volume;
        _bindAudioEvents();
      }
      state.audio.src = config.stream;

      if (R.npTrack)  R.npTrack.textContent  = config.nowPlaying || 'Live Broadcast';
      if (R.npArtist) R.npArtist.textContent = config.name       || 'CULTURE 43.2 FM';
      _setModeBadge();
    }

    /* ──────────────────────────────────────────────────────────
       AUDIO EVENTS
    ────────────────────────────────────────────────────────── */
    function _bindAudioEvents() {
      var a = state.audio;

      a.addEventListener('ended', function () {
        if (mode !== 'playlist') return;
        _handleTrackEnded();
      });

      a.addEventListener('timeupdate', function () {
        if (!a.duration || isNaN(a.duration)) return;
        var pct = (a.currentTime / a.duration) * 100;
        if (R.progressFill) R.progressFill.style.width = pct + '%';
        if (R.timeCurrent)  R.timeCurrent.textContent  = _fmt(a.currentTime);
        /* Update widget too */
        var wFill = document.getElementById('fw-prog-fill');
        if (wFill) wFill.style.width = pct + '%';
      });

      a.addEventListener('loadedmetadata', function () {
        if (R.timeTotal) R.timeTotal.textContent = _fmt(a.duration);
      });

      a.addEventListener('error', function () {
        console.warn('[FM] Track error — skipping');
        _showError('Track failed to load — skipping...');
        setTimeout(function () {
          _hideError();
          if (mode === 'playlist') _next();
        }, 1800);
      });

      a.addEventListener('waiting', function () {
        /* CHECKPOINT 22 — buffering indicator. Was a no-op ("keep UI
           in playing state") with no visual feedback at all; now
           dims the now-playing equalizer bars into a slow buffering
           pulse instead of their normal play animation, and clears
           it as soon as playback actually resumes. */
        if (R.npEq) R.npEq.classList.add('buffering');
      });
      a.addEventListener('playing', function () {
        if (R.npEq) R.npEq.classList.remove('buffering');
      });
      a.addEventListener('pause', function () {
        if (R.npEq) R.npEq.classList.remove('buffering');
      });
    }

    /* BUGFIX — "can't play music in the background anymore, ever,
       after using Pitch/Studio FX once." Root cause: createMedia
       ElementSource() permanently captures an <audio> element into
       the Web Audio graph for that element's entire lifetime — there
       is no way to un-capture it, only disconnect it (which produces
       silence, not a fallback to normal output). Mobile browsers
       (iOS Safari especially) suspend Web-Audio-routed audio the
       moment the page/app goes to the background, which kills
       background playback for that element for good — this has
       nothing to do with the Pitch-mute bug fixed elsewhere in this
       file, and no amount of on-screen "it looks fine now" undoes it,
       because the underlying element is what's affected, not
       anything visible in the UI.

       Fix: right before the page is hidden, swap live playback onto a
       brand-new, never-captured <audio> element carrying the same
       track and position. That element backgrounds exactly like
       normal audio always did, before Studio FX ever touched
       anything. Coming back to the foreground re-applies whatever
       Studio FX were dialed in (_applyStudioToNewTrack already does
       exactly this "wire up native + re-engage Pitch if needed" work
       for track changes, so it's reused as-is here). */
    function _swapToPlainAudioElement() {
      var old = state.audio;
      if (!old) return;

      var fresh = new Audio();
      fresh.preload = 'auto';
      fresh.src = old.src;
      try { fresh.currentTime = old.currentTime; } catch (e) {}
      fresh.volume = 1;
      fresh.playbackRate = old.playbackRate;
      fresh.loop = old.loop;
      var wasPlaying = !old.paused;

      state.audio = fresh;
      state.studio.wasBackgroundSwapped = true; // tells the foreground handler a real rebuild is actually needed
      _bindAudioEvents(); // re-binds ended/timeupdate/etc. onto the NEW element

      if (wasPlaying) {
        var p = fresh.play();
        if (p && p.catch) p.catch(function () {});
      }

      try { old.pause(); } catch (e) {}
      // `old` (permanently tied to the Web Audio graph) is simply
      // dropped here. state.studio.nativeSrcNode still points at it,
      // but the next time Studio FX are engaged, the staleness check
      // already in _pipeNativeIntoGraph (nativeSrcAudioEl !==
      // state.audio) rebuilds a fresh node for `fresh` automatically.
    }

    /* SINGLE visibilitychange handler for all Studio/background
       concerns — this used to be TWO separate listeners registered at
       different points in init (one here, one further down near the
       lock-screen resilience code). Both fired on every single
       visibility toggle, including brief/incidental ones (switching
       apps for a second, a permission prompt, etc.), each doing its
       own ctx.resume()/play() independently. Two listeners racing
       against each other on the same event, PLUS a full SoundTouch
       rebuild (_applyStudioToNewTrack -> _engageSoundTouch, a fresh
       fetch+decode+construct) firing on every one of those toggles
       even when no actual background swap had occurred, was a
       standing source of exactly this kind of "sometimes it just
       doesn't come back right" flakiness. Now: the swap flag makes
       the SoundTouch rebuild only happen on the toggles where a real
       swap occurred, and everything lives in one place. */
    document.addEventListener('visibilitychange', function () {
      var s = state.studio;

      if (document.hidden) {
        if (!state.audio) return;
        // Only relevant once Studio has actually captured the
        // element — plain, never-touched playback already
        // backgrounds fine on its own and doesn't need any of this.
        if (s.ctx && s.nativeSrcAudioEl === state.audio) {
          _swapToPlainAudioElement();
        }
        return;
      }

      // Back in the foreground.
      if (s.ctx && s.ctx.state === 'suspended') { try { s.ctx.resume(); } catch (e) {} }

      if (s.ctx && s.wasBackgroundSwapped) {
        // A real element swap happened while hidden — restore
        // whatever Studio FX were dialed in onto the current element.
        // Reset the flag first so a rebuild can never double-fire if
        // visibilitychange fires again before this one finishes.
        s.wasBackgroundSwapped = false;
        _applyStudioToNewTrack();
      }

      if (state.audio) {
        if (state.isPlaying && state.audio.paused) {
          var p = state.audio.play();
          if (p && p.catch) p.catch(function () {});
        }
        if (state.isPlaying) _startEqualizer();
      }
    });

    function _currentTrack() {
      return state.playlist[state.playOrder[state.currentIdx]];
    }

    /* Shared end-of-track logic — handles Repeat One/Off/All. */
    function _handleTrackEnded() {
      if (state.repeatMode === 'one') {
        if (state.audio) {
          state.audio.currentTime = 0;
          var p = state.audio.play();
          if (p && p.catch) p.catch(function () {});
        }
        return;
      }
      var isLast = state.currentIdx === state.playlist.length - 1;
      if (state.repeatMode === 'off' && isLast) {
        _pause();
        return;
      }
      _next();
    }

    /* ──────────────────────────────────────────────────────────
       PLAY / PAUSE / NEXT / PREV
    ────────────────────────────────────────────────────────── */
    /* CHECKPOINT 22 — rapid-click fix.
       Root cause: state.isPlaying only flipped to true inside the
       play() promise's .then(), which resolves asynchronously. A
       fast second click landing before that promise settled read a
       stale `isPlaying: false` and re-entered _play() again (or
       raced a _pause() against a still-pending play()), which is
       exactly the "buttons becoming inconsistent when clicked
       rapidly" bug named in this checkpoint's brief. Fix: flip
       isPlaying and the button UI optimistically and synchronously
       on every _play()/_pause() call (so back-to-back clicks always
       act on the just-clicked intent, never a stale async value),
       and stamp each call with a toggleToken so a late-resolving
       promise from a superseded call can no longer overwrite a newer
       one's UI state. */
    /* ── EQUALIZER — the little "NOW PLAYING" bars, made real ──
       Three bars (low/mid/high) driven straight off the AnalyserNode
       that's already sitting at the end of the Studio graph, so this
       reflects the ACTUAL sound (post Bass/Reverb/Pitch), not a fake
       CSS loop. Runs on rAF only while something is actually playing,
       and stops immediately on pause so it never spins for nothing. */
    var _eqRAF = null;
    var _eqBars = null;
    function _startEqualizer() {
      if (_eqRAF) return; // already running
      if (!R.npEq) return;
      if (!_eqBars) _eqBars = R.npEq.querySelectorAll('span');
      var s = state.studio;
      if (!s.analyser || !_eqBars.length) return;
      R.npEq.classList.add('audio-reactive');

      function frame() {
        if (!state.isPlaying) { _eqRAF = null; return; }
        _eqRAF = requestAnimationFrame(frame);
        s.analyser.getByteFrequencyData(s.analyserData);
        var bins = s.analyserData, n = bins.length;
        var third = Math.max(1, Math.floor(n / 3));
        for (var i = 0; i < _eqBars.length; i++) {
          var start = i * third, end = Math.min(n, start + third), sum = 0, count = 0;
          for (var j = start; j < end; j++) { sum += bins[j]; count++; }
          var avg = count ? sum / count : 0;
          var pct = Math.max(15, Math.min(100, (avg / 255) * 130)); // small floor so bars never fully flatline
          _eqBars[i].style.height = pct + '%';
        }
      }
      frame();
    }
    function _stopEqualizer() {
      if (_eqRAF) { cancelAnimationFrame(_eqRAF); _eqRAF = null; }
      if (R.npEq) {
        R.npEq.classList.remove('audio-reactive');
        _eqBars && _eqBars.forEach(function (b) { b.style.height = ''; });
      }
    }

    function _play() {
      if (!state.audio) return;

      // NOTE: ordinary playback deliberately does NOT touch the
      // Studio audio graph at all — it's plain, native <audio>.play(),
      // exactly as bulletproof as before Studio FX existed. The graph
      // only gets built the moment an actual FX control is touched
      // (see _ensureAudioGraph() call sites in the Reverb/Bass/Pitch/
      // Speed/Preset handlers below). If Studio HAS been engaged this
      // session, make sure its context isn't sitting suspended.
      if (state.studio.ctx && state.studio.ctx.state === 'suspended') {
        try { state.studio.ctx.resume(); } catch (e) {}
      }

      // BUGFIX — "Pause doesn't actually pause / Play doesn't resume
      // once Pitch has been used." When the SoundTouch engine is
      // live, the audible source is the PitchShifter pseudo-node, NOT
      // state.audio (which is just muted and kept running to drive
      // the UI). This file's connect()/disconnect() IS the shifter's
      // own play/pause per its API — nothing above ever called it, so
      // Pause left the shifter's sound running, and that "still
      // playing when it should be paused" state is exactly what
      // carried over and looked broken even after Pitch was dialed
      // back to 0. Reconnect it here, symmetric with the disconnect
      // added to _pause() below.
      if (state.studio.engine === 'soundtouch' && state.studio.shifter && state.studio.fxInput) {
        try { state.studio.shifter.connect(state.studio.fxInput); } catch (e) {}
      }

      var loadTok = state.loadToken;
      var toggleTok = ++state.toggleToken;

      state.isPlaying = true;
      _setPlayingUI(true);
      _startEqualizer();

      var p = state.audio.play();

      if (p && p.then) {
        p.then(function () {
          if (loadTok !== state.loadToken || toggleTok !== state.toggleToken) return; /* superseded */
          _updateMediaSession();
        }).catch(function (err) {
          if (loadTok !== state.loadToken || toggleTok !== state.toggleToken) return; /* superseded — not a real error */
          if (err.name === 'AbortError') return;  /* expected when switching tracks/toggling quickly */
          state.isPlaying = false;
          _setPlayingUI(false);
          if (err.name === 'NotAllowedError') {
            _showError('Tap Play to start — browser requires a tap first.');
          } else {
            _showError('Playback error. Check your stream URL in lf-config.liquid.');
            console.warn('[FM]', err);
          }
        });
      } else {
        /* Older browsers with no promise-based play() */
        _updateMediaSession();
      }
    }

    function _pause() {
      ++state.toggleToken; /* invalidate any pending _play() promise from before this pause */
      if (state.audio) state.audio.pause();

      // Same fix as _play() above, in reverse: disconnecting the
      // PitchShifter IS how this library pauses it. Without this,
      // clicking Pause while Pitch was active never actually stopped
      // the sound.
      if (state.studio.engine === 'soundtouch' && state.studio.shifter) {
        try { state.studio.shifter.disconnect(); } catch (e) {}
      }

      state.isPlaying = false;
      _setPlayingUI(false);
      _stopEqualizer();
      _updateMediaSessionState();
    }

    function _toggle() {
      if (state.isPlaying) _pause(); else _play();
    }

    var _lastNavAt = 0;
    function _navGuard() {
      var now = Date.now();
      if (now - _lastNavAt < 250) return false; /* too fast — let the current load/play settle first */
      _lastNavAt = now;
      return true;
    }

    function _next() {
      if (!state.playlist.length || !_navGuard()) return;
      var nextIdx = (state.currentIdx + 1) % state.playlist.length;
      _loadTrack(nextIdx, state.isPlaying);
    }

    function _prev() {
      if (!state.playlist.length || !_navGuard()) return;
      /* If >3s in, restart; else go to prev track */
      if (state.audio && state.audio.currentTime > 3) {
        state.audio.currentTime = 0;
        return;
      }
      var prevIdx = (state.currentIdx - 1 + state.playlist.length) % state.playlist.length;
      _loadTrack(prevIdx, state.isPlaying);
    }

    function _toggleShuffle() {
      state.isShuffle = !state.isShuffle;
      _buildPlayOrder();
      if (R.shuffleBtn) R.shuffleBtn.classList.toggle('active', state.isShuffle);
      _updateQueue();
    }

    /* Repeat: Off -> All -> One -> Off. 'All' matches the player's
       original always-loop-the-playlist behavior, so it stays the
       default — nothing changes for anyone who never touches this
       button. */
    var REPEAT_ORDER = ['all', 'one', 'off'];
    var REPEAT_LABEL = { all: 'Repeat', one: 'Repeat One', off: 'No Repeat' };
    function _cycleRepeat() {
      var i = REPEAT_ORDER.indexOf(state.repeatMode);
      state.repeatMode = REPEAT_ORDER[(i + 1) % REPEAT_ORDER.length];
      if (R.repeatBtn) {
        R.repeatBtn.classList.toggle('active', state.repeatMode !== 'off');
        R.repeatBtn.title = 'Repeat: ' + (state.repeatMode === 'all' ? 'All' : state.repeatMode === 'one' ? 'One' : 'Off');
      }
      if (R.repeatLabel) R.repeatLabel.textContent = REPEAT_LABEL[state.repeatMode];
    }

    function _seek(e) {
      if (!state.audio || !state.audio.duration) return;
      var rect = R.progressTrack.getBoundingClientRect();
      var pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      state.audio.currentTime = pct * state.audio.duration;
    }

    /* Live-drag scrub (checkpoint 26 — "smoother, never select page
       elements, never interrupt playback, never freeze audio"):
       the fill/time UI updates on every pointermove for an instantly
       responsive drag, but the actual audio.currentTime write (the
       expensive part — real seeking) is batched to once per animation
       frame instead of once per mousemove, which on a fast drag could
       fire dozens of times a second and stutter/stall playback. */
    var _scrubRAF = null, _scrubPct = null;
    function _seekPreview(clientX) {
      if (!R.progressTrack) return;
      var rect = R.progressTrack.getBoundingClientRect();
      _scrubPct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      if (R.progressFill) R.progressFill.style.width = (_scrubPct * 100) + '%';

      if (!state.audio || !state.audio.duration) return;
      if (R.timeCurrent) R.timeCurrent.textContent = _fmt(_scrubPct * state.audio.duration);
      if (_scrubRAF) return;
      _scrubRAF = requestAnimationFrame(function () {
        _scrubRAF = null;
        if (_scrubPct !== null && state.audio && state.audio.duration) {
          state.audio.currentTime = _scrubPct * state.audio.duration;
        }
      });
    }

    /* ──────────────────────────────────────────────────────────
       UI UPDATES
    ────────────────────────────────────────────────────────── */
    function _setPlayingUI(on) {
      R.root.classList.toggle('is-playing', on);
      if (R.playBtn)       R.playBtn.classList.toggle('playing', on);
      if (R.playIcon)      R.playIcon.innerHTML = on ? LF_ICON_PAUSE : LF_ICON_PLAY;
      if (R.liveIndicator) R.liveIndicator.classList.toggle('active', on);
      if (R.npEq)          R.npEq.classList.toggle('playing', on);

      /* Sync widget if open in same tab (same-origin iframe unlikely but handle anyway) */
      var wBtn = document.getElementById('fw-play');
      var wIcon = document.getElementById('fw-icon');
      if (wBtn)  wBtn.classList.toggle('playing', on);
      if (wIcon) wIcon.innerHTML = on ? LF_ICON_PAUSE : LF_ICON_PLAY;

      /* Visualizer */
      if (R.visWrap) R.visWrap.classList.toggle('playing', on);
    }

    function _updateNowPlaying(track) {
      if (R.npTrack)  R.npTrack.textContent  = track.title  || 'Unknown';
      if (R.npArtist) R.npArtist.textContent = track.artist || '';

      /* Sync widget elements on same page */
      var wTrack  = document.getElementById('fw-track');
      var wArtist = document.getElementById('fw-artist');
      if (wTrack)  wTrack.textContent  = track.title  || 'Unknown';
      if (wArtist) wArtist.textContent = track.artist || '';
    }

    /* CHECKPOINT 20 FIX: this used to hard-cap at `Math.min(total, 10)`,
       so bulk-uploaded libraries past 10 tracks never appeared in the
       Up Next list even though they were fully loaded and playable.
       The queue panel already scrolls (max-height + overflow-y:auto in
       lf-fm.css), so the full list is shown here. */
    function _updateQueue() {
      if (!R.queueList) return;
      R.queueList.innerHTML = '';
      var total = state.playlist.length;
      if (total === 0) {
        if (R.queueCount) R.queueCount.textContent = '0 TRACKS';
        return;
      }

      /* CHECKPOINT 23 — SEARCH ("Add search. Search by: Song, Artist.
         Realtime filtering."). Filters which queue rows render; does
         not touch playback/playOrder, so search never changes what's
         actually queued up next — only what's visible while you look
         for something. */
      var q = (state.searchQuery || '').trim().toLowerCase();
      var shown = 0;

      for (var i = 0; i < total; i++) {
        var orderIdx = (state.currentIdx + i) % total;
        var track    = state.playlist[state.playOrder[orderIdx]];
        if (!track) continue;

        if (q) {
          var hay = ((track.title || '') + ' ' + (track.artist || '')).toLowerCase();
          if (hay.indexOf(q) === -1) continue;
        }

        var item = document.createElement('div');
        item.className = 'fm-queue-item' + (i === 0 ? ' active' : '');
        item.innerHTML =
          '<span class="fm-qi-num">' + (i === 0 ? LF_ICON_PLAY_SM : i) + '</span>' +
          '<span class="fm-qi-title">' + _esc(track.title || 'Unknown') + '</span>' +
          '<span class="fm-qi-artist">' + _esc(track.artist || '') + '</span>';

        /* Closure for click */
        (function (oi, isPlaying) {
          item.addEventListener('click', function () {
            _loadTrack(oi, isPlaying);
          });
        }(orderIdx, state.isPlaying));

        R.queueList.appendChild(item);
        shown++;
      }

      if (q && shown === 0) {
        var empty = document.createElement('div');
        empty.className = 'fm-queue-empty';
        empty.textContent = 'No songs or artists match "' + q + '".';
        R.queueList.appendChild(empty);
      }

      if (R.queueCount) {
        R.queueCount.textContent = q
          ? shown + ' MATCH' + (shown === 1 ? '' : 'ES')
          : total + (total === 1 ? ' TRACK' : ' TRACKS');
      }
    }

    function _resetProgress() {
      if (R.progressFill) R.progressFill.style.width = '0%';
      if (R.timeCurrent)  R.timeCurrent.textContent  = '0:00';
      if (R.timeTotal)    R.timeTotal.textContent    = '0:00';
    }

    function _setModeBadge() {
      if (!R.modeBadge) return;
      if (mode === 'stream') {
        R.modeBadge.innerHTML  = '<span class="fm-mode-dot"></span> LIVE STREAM';
        R.modeBadge.className   = 'fm-mode-badge stream';
      } else if (state.source === 'mix') {
        R.modeBadge.innerHTML  = LF_ICON_MIX_SM + ' MIXES';
        R.modeBadge.className   = 'fm-mode-badge playlist';
      } else {
        R.modeBadge.innerHTML  = '<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden="true"><path fill="currentColor" d="M8 5v14l11-7z"/></svg> PLAYLIST';
        R.modeBadge.className   = 'fm-mode-badge playlist';
      }
    }

    function _showEmptyState(source) {
      if (R.playerCore) R.playerCore.style.display = 'none';
      if (R.emptyState) R.emptyState.style.display = (source === 'mix') ? 'none' : 'block';
      if (R.mixesEmpty) R.mixesEmpty.style.display = (source === 'mix') ? 'block' : 'none';
    }
    function _hideEmptyState() {
      if (R.playerCore) R.playerCore.style.display = '';
      if (R.emptyState) R.emptyState.style.display = 'none';
      if (R.mixesEmpty) R.mixesEmpty.style.display = 'none';

      /* FORCE UI REFRESH */
      if (state.playlist.length > 0) {
      R.playerCore.style.display = '';
    }
}

    function _showError(msg) {
      var el = document.getElementById('fm-error-msg');
      if (el) { el.textContent = msg; el.style.display = 'block'; }
    }
    function _hideError() {
      var el = document.getElementById('fm-error-msg');
      if (el) el.style.display = 'none';
    }

    /* ──────────────────────────────────────────────────────────
       VISUALIZER
       ── FIX: this used to route state.audio through a Web Audio
       AudioContext (createMediaElementSource → analyser → destination)
       to drive real FFT-based bar heights. Once an <audio> element is
       connected via createMediaElementSource, ALL of its output is
       permanently re-routed through that graph — nothing plays unless
       every node in the chain (and the context itself) behaves
       exactly right. In practice, cross-origin source files (Shopify
       CDN) + AudioContext autoplay-policy edge cases made this
       silently fail on the full FM page while the widget — which
       never touches Web Audio at all, just `new Audio()` + `.play()`
       — always worked. That was the actual root cause of the "no
       sound on the page, but the widget plays fine" bug.
       Fix: the page's audio element is now played the exact same
       untouched way the widget does it. The bars are purely CSS-
       driven (`.fm-vis-wrap.playing .fm-bar` → `fmPlay` keyframe,
       already defined in lf-ecosystem.css and already toggled by
       _setPlayingUI above) — same technique the widget's `.fm-w-vis`
       bars use. No audio node is ever created, so nothing can mute it.
    ────────────────────────────────────────────────────────── */

    /* ──────────────────────────────────────────────────────────
       MEDIA SESSION — real OS/browser-native media controls
       (lock screen, keyboard media keys, Chrome media hub, macOS
       Control Center / Windows SMTC "now playing"). This is what
       was actually missing for desktop media controls to appear —
       without it, custom <audio>-based players have no way to
       surface controls to the OS at all.
    ────────────────────────────────────────────────────────── */
    function _updateMediaSession() {
      if (!('mediaSession' in navigator) || !state.audio) return;
      try {
        var track = state.playlist[state.playOrder[state.currentIdx]] || {};
        navigator.mediaSession.metadata = new MediaMetadata({
          title:  track.title  || (mode === 'stream' ? (config.nowPlaying || 'Live Broadcast') : 'CULTURE 43.2 FM'),
          artist: track.artist || config.name || 'LIVEFASTCULTURE',
          album:  'CULTURE 43.2 FM',
          artwork: config.artwork ? [{ src: config.artwork, sizes: '512x512', type: 'image/png' }] : []
        });
        navigator.mediaSession.setActionHandler('play',  function () { _play(); });
        navigator.mediaSession.setActionHandler('pause', function () { _pause(); });
        if (mode === 'playlist') {
          navigator.mediaSession.setActionHandler('previoustrack', function () { _prev(); });
          navigator.mediaSession.setActionHandler('nexttrack',     function () { _next(); });
        }
        _updateMediaSessionState();
      } catch (e) { /* MediaSession unsupported — no-op */ }
    }
    function _updateMediaSessionState() {
      if (!('mediaSession' in navigator)) return;
      try { navigator.mediaSession.playbackState = state.isPlaying ? 'playing' : 'paused'; } catch (e) {}
    }

    /* ──────────────────────────────────────────────────────────
       CLOCK
    ────────────────────────────────────────────────────────── */
    function _startClock() {
      if (!R.clockEl) return;
      function tick() {
        var n = new Date();
        var h = String(n.getHours()).padStart(2,'0');
        var m = String(n.getMinutes()).padStart(2,'0');
        var s = String(n.getSeconds()).padStart(2,'0');
        R.clockEl.textContent = h + ':' + m + ':' + s;
      }
      tick();
      setInterval(tick, 1000);
    }

    /* ──────────────────────────────────────────────────────────
       BIND CONTROLS
    ────────────────────────────────────────────────────────── */
    function _bindControls() {
      if (R.playBtn)       R.playBtn.addEventListener('click', _toggle);
      if (R.prevBtn)       R.prevBtn.addEventListener('click', _prev);
      if (R.nextBtn)       R.nextBtn.addEventListener('click', _next);
      if (R.shuffleBtn)    R.shuffleBtn.addEventListener('click', _toggleShuffle);
      if (R.repeatBtn)     R.repeatBtn.addEventListener('click', _cycleRepeat);

      if (R.queueBtn) {
        R.queueBtn.addEventListener('click', function () {
          if (R.queuePanel) R.queuePanel.classList.toggle('open');
          R.queueBtn.classList.toggle('open');
        });
      }

      /* ── BACKGROUND / LOCK-SCREEN RESILIENCE ──────────────────
         Mobile OSes and some browsers can suspend an AudioContext
         when the tab is backgrounded or the screen locks. The
         ctx-resume + play-resume + equalizer-restart logic that used
         to live in a second visibilitychange listener here has been
         folded into the single consolidated listener defined earlier
         (search "SINGLE visibilitychange handler") — two listeners on
         the same event were racing each other on every foreground
         return. pageshow is a genuinely distinct event (fires on
         back/forward-cache restores that visibilitychange doesn't
         always catch), so that one stays. */
      window.addEventListener('pageshow', function () {
        var ctx = state.studio.ctx;
        if (ctx && ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }
      });

      /* ── STUDIO FX PANEL — pure UI toggle. It never builds or
         touches the audio graph itself — that only ever happens the
         moment an actual control (a slider, a step button, a preset)
         is touched, so opening/closing this panel can never be what
         breaks or interrupts playback.

         Closing the panel intentionally resets every FX back to
         neutral (Reverb/Bass/Pitch/Speed) — "exit Studio = clean
         playback" — same non-pausing engine-switch used everywhere
         else, so the song never stops or jumps, it just stops being
         processed. */
      if (R.studioBtn) {
        R.studioBtn.addEventListener('click', function () {
          var opening = !R.studioPanel.classList.contains('open');
          if (R.studioPanel) R.studioPanel.classList.toggle('open');
          R.studioBtn.classList.toggle('open');

          if (opening) {
            _syncStudioUI();
          } else {
            _setBassGain(0);
            _setReverbWet(0);
            state.studio.speed = 1;
            if (state.studio.pitch !== 0) { state.studio.pitch = 0; _engageNative(); }
            else if (state.studio.ctx) { _applyNativeRate(); }
            _syncStudioUI();
            _saveStudioPrefs();
            if (R.presetBtns) R.presetBtns.forEach(function (b) { b.classList.remove('active'); });
          }
        });
      }

      if (R.studioReset) {
        R.studioReset.addEventListener('click', function () {
          _setBassGain(0);
          _setReverbWet(0);
          state.studio.speed = 1;
          if (state.studio.pitch !== 0) { state.studio.pitch = 0; _engageNative(); }
          else { _applyNativeRate(); }
          _syncStudioUI();
          _saveStudioPrefs();
          if (R.presetBtns) R.presetBtns.forEach(function (b) { b.classList.remove('active'); });
        });
      }

      if (R.reverbSlider) {
        R.reverbSlider.addEventListener('input', function () {
          _ensureAudioGraph();
          _setReverbWet(parseFloat(this.value) / 100);
          _syncStudioUI();
          _saveStudioPrefs();
        });
      }

      if (R.bassSlider) {
        R.bassSlider.addEventListener('input', function () {
          _ensureAudioGraph();
          _setBassGain(parseFloat(this.value));
          _syncStudioUI();
          _saveStudioPrefs();
        });
      }

      /* Speed / Pitch — Spotify/Apple-style step buttons, not sliders.
         Each press is one discrete step; nothing here ever pauses or
         reloads the track (see _stepSpeed/_stepPitch). */
      if (R.speedDownBtn) R.speedDownBtn.addEventListener('click', function () { _stepSpeed(-1); _saveStudioPrefs(); });
      if (R.speedUpBtn)   R.speedUpBtn.addEventListener('click',   function () { _stepSpeed(1);  _saveStudioPrefs(); });
      if (R.pitchDownBtn) R.pitchDownBtn.addEventListener('click', function () { _stepPitch(-1); _saveStudioPrefs(); });
      if (R.pitchUpBtn)   R.pitchUpBtn.addEventListener('click',   function () { _stepPitch(1);  _saveStudioPrefs(); });

      /* One-tap FX presets (Nightcore, Slowed + Reverb, Vinyl, etc.) */
      if (R.presetBtns) {
        R.presetBtns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            _applyPreset(btn.getAttribute('data-preset'));
          });
        });
      }

      if (R.studioDownload) {
        R.studioDownload.addEventListener('click', _downloadWithFX);
      }

      /* Playlist / Mixes source switch — instant, no reload */
      if (R.srcPlaylistBtn) {
        R.srcPlaylistBtn.addEventListener('click', function () {
          if (state.source === 'playlist') return;
          var wasPlaying = state.isPlaying;
          if (window.setFMMode) window.setFMMode('playlist');
          _loadSource('playlist', wasPlaying);
        });
      }
      if (R.srcMixBtn) {
        R.srcMixBtn.addEventListener('click', function () {
          if (state.source === 'mix') return;
          var wasPlaying = state.isPlaying;
          if (window.setFMMode) window.setFMMode('mix');
          _loadSource('mix', wasPlaying);
        });
      }

      if (R.progressTrack) {
        R.progressTrack.addEventListener('click', _seek);
        /* CHECKPOINT 23/26 — draggable, selection-safe, stutter-free
           progress scrub. mousedown starts tracking and calls
           preventDefault so a fast drag never triggers the browser's
           native text-selection (dragging across a page with a plain
           mousedown+mousemove otherwise highlights everything under
           the cursor). mousemove is bound on the document, not just
           the track, so dragging off the edges doesn't drop the
           scrub. Actual seeking is rAF-batched inside _seekPreview so
           dragging stays smooth and never freezes/interrupts audio. */
        R.progressTrack.addEventListener('mousedown', function (e) {
          e.preventDefault();
          document.body.classList.add('fm-scrubbing-lock');
          if (R.progressFill) R.progressFill.classList.add('is-scrubbing');
          _seekPreview(e.clientX);
          function onMove(ev) { _seekPreview(ev.clientX); }
          function onUp() {
            document.body.classList.remove('fm-scrubbing-lock');
            if (R.progressFill) R.progressFill.classList.remove('is-scrubbing');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
          }
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        });
        /* Touch support — drag, not just tap */
        R.progressTrack.addEventListener('touchstart', function (e) {
          e.preventDefault();
          if (R.progressFill) R.progressFill.classList.add('is-scrubbing');
          _seekPreview(e.touches[0].clientX);
        }, { passive: false });
        R.progressTrack.addEventListener('touchmove', function (e) {
          e.preventDefault();
          _seekPreview(e.touches[0].clientX);
        }, { passive: false });
        R.progressTrack.addEventListener('touchend', function () {
          if (R.progressFill) R.progressFill.classList.remove('is-scrubbing');
        });
      }

      if (R.volSlider) {
        R.volSlider.value = state.volume;
        R.volSlider.addEventListener('input', function () {
          state.volume = parseFloat(this.value);
          _setMasterVolume(state.volume);
        });
      }

      /* CHECKPOINT 23 — SEARCH (realtime, no submit/debounce needed at
         this list size) */
      if (R.searchInput) {
        R.searchInput.addEventListener('input', function () {
          state.searchQuery = this.value;
          _updateQueue();
        });
      }

      /* Copy link (checkpoint 18 — replaces the old iframe embed-code
         generator; see page.fm.liquid "SHARE SECTION" comment for the
         rationale). Plain URL only — nothing to paste/maintain on a
         third-party site, just a link that opens the real station. */
      if (R.copyBtn && R.embedCode) {
        var stationUrl = window.location.origin + '/pages/fm';
        R.embedCode.textContent = stationUrl;
        R.copyBtn.addEventListener('click', function () {
          navigator.clipboard.writeText(stationUrl).then(function () {
            var orig = R.copyBtn.textContent;
            R.copyBtn.textContent = 'LINK COPIED ✓';
            setTimeout(function () { R.copyBtn.textContent = orig; }, 2000);
          }).catch(function () {
            var r = document.createRange();
            r.selectNodeContents(R.embedCode);
            window.getSelection().removeAllRanges();
            window.getSelection().addRange(r);
          });
        });
      }

      /* Share */
      if (R.shareBtn) {
        R.shareBtn.addEventListener('click', function () {
          var d = { title: config.name || 'CULTURE 43.2 FM', url: window.location.href };
          if (navigator.share) { navigator.share(d).catch(function(){}); }
          else {
            navigator.clipboard.writeText(window.location.href).then(function () {
              var orig = R.shareBtn.textContent;
              R.shareBtn.textContent = 'LINK COPIED ✓';
              setTimeout(function () { R.shareBtn.textContent = orig; }, 2000);
            });
          }
        });
      }

      /* CHECKPOINT 22 — Upload to Story.
         No public Instagram web API posts directly into a Story —
         the honest path is handing off to the OS share sheet (same
         mechanism as Share Station above) so the person can pick
         Instagram themselves. This is the "build the UI, document
         the limitation" version named in the brief, not a fake
         one-tap Story post. */
      if (R.storyBtn) {
        R.storyBtn.addEventListener('click', function () {
          var trackName = (state.playlist[state.currentIdx] && state.playlist[state.currentIdx].title) || (config.name || 'CULTURE 43.2 FM');
          var d = { title: trackName, text: 'Listening on ' + (config.name || 'CULTURE 43.2 FM'), url: window.location.href };
          if (navigator.share) {
            navigator.share(d).catch(function () {});
          } else {
            navigator.clipboard.writeText(window.location.href).then(function () {
              var orig = R.storyBtn.textContent;
              R.storyBtn.textContent = 'LINK COPIED ✓';
              setTimeout(function () { R.storyBtn.textContent = orig; }, 2000);
            });
          }
        });
      }
    }

    /* ──────────────────────────────────────────────────────────
       HELPERS
    ────────────────────────────────────────────────────────── */
    function _fmt(secs) {
      if (!secs || isNaN(secs)) return '0:00';
      var m = Math.floor(secs / 60);
      var s = Math.floor(secs % 60);
      return m + ':' + String(s).padStart(2, '0');
    }

    function _esc(str) {
      return String(str)
        .replace(/&/g,'&amp;').replace(/</g,'&lt;')
        .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    /* Stagger bar animations */
    state.bars.forEach(function (bar, i) {
      bar.style.animationDelay = (i * 0.035) + 's';
    });

    /* ── BOOT ── */
    init();

  }); /* end DOMContentLoaded */


  /* ════════════════════════════════════════════════════════════
     WIDGET PAGE PLAYER (for /pages/fm-widget)
     Same checkpoint-20 fix applied: awaits window.LF_FM_READY
     before reading window.LF_PLAYLIST, instead of reading it
     synchronously on DOMContentLoaded.
  ════════════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function () {
    if (window.__lfFmWidgetBound) return;

    var wRoot = document.getElementById('fw-root');
    if (!wRoot) return;
    window.__lfFmWidgetBound = true;

    (async function () {
      await fmReady();

      var playlist = validTracks(window.LF_PLAYLIST);
      var config   = (window.LF_CONFIG && window.LF_CONFIG.fm) || {};
      var settings = window.LF_FM_SETTINGS || {};
      var hasStream = config.stream && config.stream.indexOf('YOUR-STREAM-ID') === -1;
      var mode = (settings.mode === 'stream' && hasStream) ? 'stream' : 'playlist';

      var audio    = null;
      var isPlay   = false;
      var curIdx   = 0;
      /* CHECKPOINT 22B (real fix — a prior pass claimed this was done
         but the shipped file never actually got it): same toggleToken
         pattern as the main player's _play()/_pause() above. Without
         it, a fast double-click on fw-play before audio.play()'s
         promise settled could leave isPlay/the icon out of sync with
         real playback, since isPlay flips synchronously but play()
         resolves later. */
      var wToggleToken = 0;

      var wPlay   = document.getElementById('fw-play');
      var wIcon   = document.getElementById('fw-icon');
      var wTrack  = document.getElementById('fw-track');
      var wArtist = document.getElementById('fw-artist');
      var wFill   = document.getElementById('fw-prog-fill');
      var wVis    = document.getElementById('fw-vis');

      /* Set initial track info */
      if (playlist.length > 0) {
        var first = playlist[0];
        if (wTrack)  wTrack.textContent  = first.title  || 'Culture Programming';
        if (wArtist) wArtist.textContent = first.artist || 'CULTURE 43.2 FM';
      } else if (config.nowPlaying) {
        if (wTrack)  wTrack.textContent  = config.nowPlaying;
        if (wArtist) wArtist.textContent = config.name || 'CULTURE 43.2 FM';
      }

      if (!wPlay) return;

      wPlay.addEventListener('click', function () {
        var thisToggle = ++wToggleToken;
        if (isPlay) {
          if (audio) { audio.pause(); }
          isPlay = false;
          wPlay.classList.remove('playing');
          if (wIcon)  wIcon.innerHTML = LF_ICON_PLAY;
          if (wVis)   wVis.classList.remove('playing');
        } else {
          if (!audio) {
            audio = new Audio();
            audio.volume = 0.8;
            if (mode === 'stream') {
              audio.src = config.stream;
            } else if (playlist[curIdx]) {
              audio.src = playlist[curIdx].src;
              audio.addEventListener('ended', function () {
                curIdx = (curIdx + 1) % playlist.length;
                audio.src = playlist[curIdx].src;
                audio.play();
                if (wTrack)  wTrack.textContent  = playlist[curIdx].title  || '';
                if (wArtist) wArtist.textContent = playlist[curIdx].artist || '';
              });
              audio.addEventListener('timeupdate', function () {
                if (!audio.duration || isNaN(audio.duration)) return;
                var pct = (audio.currentTime / audio.duration) * 100;
                if (wFill) wFill.style.width = pct + '%';
              });
            }
          }
          audio.play().then(function () {
            if (thisToggle !== wToggleToken) return; /* superseded by a later click */
          }).catch(function (e) {
            if (thisToggle !== wToggleToken) return; /* superseded — not a real error */
            if (e.name === 'AbortError') return; /* expected when toggling quickly */
            isPlay = false;
            wPlay.classList.remove('playing');
            if (wIcon) wIcon.textContent = '!';
            setTimeout(function () { if (wIcon) wIcon.innerHTML = LF_ICON_PLAY; }, 2000);
            console.warn('[FM widget]', e);
          });
          /* Flip UI optimistically/synchronously (not inside the promise
             callback above) so a rapid second click always reads the
             just-clicked intent instead of a stale isPlay value. */
          isPlay = true;
          wPlay.classList.add('playing');
          if (wIcon) wIcon.innerHTML = LF_ICON_PAUSE;
          if (wVis)  wVis.classList.add('playing');

          if ('mediaSession' in navigator) {
            try {
              var t = playlist[curIdx] || {};
              navigator.mediaSession.metadata = new MediaMetadata({
                title:  t.title  || config.nowPlaying || 'CULTURE 43.2 FM',
                artist: t.artist || config.name || 'LIVEFASTCULTURE',
                album:  'CULTURE 43.2 FM'
              });
              navigator.mediaSession.setActionHandler('play',  function () { audio.play(); isPlay = true; wPlay.classList.add('playing'); if (wIcon) wIcon.innerHTML = LF_ICON_PAUSE; if (wVis) wVis.classList.add('playing'); });
              navigator.mediaSession.setActionHandler('pause', function () { audio.pause(); isPlay = false; wPlay.classList.remove('playing'); if (wIcon) wIcon.innerHTML = LF_ICON_PLAY; if (wVis) wVis.classList.remove('playing'); });
              navigator.mediaSession.playbackState = 'playing';
            } catch (e) {}
          }
        }
      });
    }());
  });

}());
